/**
 * Открытие виджета Манго (Mango Communication Widget) по клику на стороннюю кнопку.
 *
 * Своего публичного API открытия у виджета нет: `Mango.MultiChannel.Api` умеет только
 * `setAdditionalFields`, а dispatch в `Mango.MultiChannel.Store` меняет стейт, но React
 * не перерисовывается. Поэтому повторяем то же действие, что и клик по его собственной
 * зелёной кнопке — она и открывает форму обратного звонка.
 */

// Кнопка виджета в состоянии `default`. Порядок — от точного id к общим классам.
const CALLBACK_BUTTON_SELECTORS = [
  '#mgo-mcw-cb-button',
  '#mgo-mcw-widget .mgo-mcw__button_cb',
  '#mgo-mcw-widget .mgo-mcw__callback-button',
];

// Признаки уже открытой формы обратного звонка.
const OPENED_MARKERS = ['#mgo-mcw-phone-input', '#mgo-mcw-call-button'];

const READY_TIMEOUT = 3000;
const RETRY_INTERVAL = 200;

const findCallbackButton = (): HTMLElement | null => {
  for (const selector of CALLBACK_BUTTON_SELECTORS) {
    const button = document.querySelector<HTMLElement>(selector);

    if (button) {
      return button;
    }
  }

  return null;
};

const isWidgetOpened = (): boolean => OPENED_MARKERS.some(selector => !!document.querySelector(selector));

/**
 * Открывает форму обратного звонка виджета Манго.
 *
 * Скрипт виджета грузится асинхронно, поэтому кнопку ждём до READY_TIMEOUT.
 *
 * @returns true, если виджет открыт (или уже был открыт); false — если не дождались.
 */
export const openMangoWidget = (): Promise<boolean> =>
  new Promise(resolve => {
    if (!process.client) {
      resolve(false);
      return;
    }

    if (isWidgetOpened()) {
      resolve(true);
      return;
    }

    const startedAt = Date.now();
    let settled = false;

    const finish = (opened: boolean) => {
      if (settled) {
        return;
      }

      settled = true;
      resolve(opened);
    };

    const attempt = () => {
      if (settled) {
        return;
      }

      const button = findCallbackButton();

      if (button) {
        try {
          button.click();
          finish(true);
        } catch {
          finish(false);
        }

        return;
      }

      if (Date.now() - startedAt >= READY_TIMEOUT) {
        finish(false);
        return;
      }

      window.setTimeout(attempt, RETRY_INTERVAL);
    };

    attempt();
  });
